import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Card, 
  CardContent, 
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  School as SchoolIcon,
  People as PeopleIcon,
  CameraAlt as CameraIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import DashboardCard from '../components/DashboardCard';
import api from '../api/api';
import InvoiceCreateDialog from "./InvoiceCreateDialog";
import { useSnackbar } from 'notistack';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface SystemStats {
  totalSchools: number;
  totalUsers: number;
  totalStudents: number;
  totalCameras: number;
  totalAttendance: number;
  activeSchools: number;
  systemHealth: 'excellent' | 'good' | 'warning' | 'critical';
}

interface School {
  id: number;
  name: string;
  address?: string;
  contact_info?: string;
  attendance_mode: string;
  created_at?: string;
}

interface User {
  id: number;
  username: string;
  role: string;
  school_id?: number;
  school_name?: string;
}

interface RecognitionStats {
  total_events: number;
  unique_students: number;
  average_confidence: number;
  events_by_camera: Array<{ camera_id: number; count: number }>;
  total_revenue: number; // Added for total revenue
  mrr: number; // Added for Monthly Recurring Revenue
  arr: number; // Added for Annual Recurring Revenue
  nrr: number | null; // Added for Net Revenue Retention
  revenue_by_day: Array<{ date: string; revenue: number }>; // Added for revenue by day
  nrr_trend: Array<{ month: string; nrr: number }>; // Added for NRR trend
}

interface SchoolApplication {
  id: number;
  school_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
  message?: string;
  status: string;
  created_at: string;
  reviewed_at?: string;
  reviewed_by?: number;
}

const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [schools, setSchools] = useState<School[]>([]);
  const [recognitionStats, setRecognitionStats] = useState<RecognitionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [recentApplications, setRecentApplications] = useState<SchoolApplication[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<SchoolApplication | null>(null);
  const [reviewStatus, setReviewStatus] = useState<'approved' | 'rejected' | ''>('');
  const [reviewMessage, setReviewMessage] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  useEffect(() => {
    if (user?.role === 'superuser') {
      fetchDashboardData();
      fetchRecentApplications();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Fetch all data in parallel
      const [schoolsRes, usersRes, recognitionRes] = await Promise.all([
        api.get('/schools/'),
        api.get('/users/'),
        api.get('/events/stats/summary?days=7')
      ]);

      const schoolsData = schoolsRes.data;
      const usersData = usersRes.data;
      const recognitionData = recognitionRes.data;

      // Calculate system stats
      const totalStudents = (usersData as any[]).filter((u: User) => u.role === 'user').length;

      // Get camera count (this would need a separate API call)
      let totalCameras = 0;
      try {
        const camerasRes = await api.get('/cameras/');
        totalCameras = (camerasRes.data as any[]).length;
      } catch (err) {
        console.warn('Could not fetch camera count:', err);
      }

      setStats({
        totalSchools: (schoolsData as any[]).length,
        totalUsers: (usersData as any[]).length,
        totalStudents,
        totalCameras,
        totalAttendance: (recognitionData as any).total_events || 0,
        activeSchools: (schoolsData as any[]).length, // Simplified - could be enhanced with actual activity data
        systemHealth: 'good' // Simplified - could be enhanced with actual health checks
      });

      setSchools(schoolsData as any[]);
      setRecognitionStats(recognitionData as any);

    } catch (err: any) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentApplications = async () => {
    setApplicationsLoading(true);
    try {
      const res = await api.get('/schools/applications?status=pending&limit=5');
      setRecentApplications(res.data as any[]);
                } catch (err) {
        setRecentApplications([]);
      } finally {
        setApplicationsLoading(false);
    }
  };

  const openReviewModal = (application: SchoolApplication) => {
    setSelectedApplication(application);
    setReviewStatus('');
    setReviewMessage('');
    setReviewModalOpen(true);
  };

  const closeReviewModal = () => {
    setReviewModalOpen(false);
    setSelectedApplication(null);
    setReviewStatus('');
    setReviewMessage('');
  };

  const handleReviewSubmit = async () => {
    if (!selectedApplication || !reviewStatus) return;
    setReviewSubmitting(true);
    try {
      await api.post(`/schools/applications/${selectedApplication.id}/review`, {
        status: reviewStatus,
        message: reviewMessage,
      });
      enqueueSnackbar(`Application ${reviewStatus === 'approved' ? 'approved' : 'rejected'} successfully`, { variant: 'success' });
      closeReviewModal();
      fetchRecentApplications();
    } catch (err: any) {
      enqueueSnackbar('Failed to submit review', { variant: 'error' });
    } finally {
      setReviewSubmitting(false);
    }
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'excellent': return '#4caf50';
      case 'good': return '#8bc34a';
      case 'warning': return '#ff9800';
      case 'critical': return '#f44336';
      default: return '#757575';
    }
  };

  // Use real revenue data from backend
  const revenueData = recognitionStats?.revenue_by_day || [];

  // Add helper function for date formatting
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (user?.role !== 'superuser') {
    return <Alert severity="error">You are not authorized to view this page.</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Super Admin Dashboard"
        subtitle="Platform overview and controls"
        action={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDashboardData}
          >
            Refresh
          </Button>
        }
      />

      {/* Summary Bar for MRR, ARR, NRR */}
      <Paper sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, p: 2, mb: 3, background: '#F3F4F6' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary">MRR</Typography>
          <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 700 }}>
            KES {recognitionStats?.mrr?.toLocaleString() || 0}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary">ARR</Typography>
          <Typography variant="h6" sx={{ color: '#388e3c', fontWeight: 700 }}>
            KES {recognitionStats?.arr?.toLocaleString() || 0}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary">NRR</Typography>
          <Typography variant="h6" sx={{ color: '#f57c00', fontWeight: 700 }}>
            {recognitionStats?.nrr !== null && recognitionStats?.nrr !== undefined ? `${recognitionStats.nrr}%` : 'N/A'}
          </Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 3, 
        mb: 3,
        '& > *': {
          flex: '1 1 200px',
          minWidth: '200px'
        }
      }}>
        <DashboardCard
          title="Total Revenue"
          value={recognitionStats?.total_revenue ? `KES ${recognitionStats.total_revenue.toLocaleString()}` : 'KES 0'}
          icon={<PaymentIcon />}
          color="#1976d2"
          onClick={() => navigate('/invoices')}
        />
        <DashboardCard
          title="Total Schools"
          value={stats?.totalSchools || 0}
          icon={<SchoolIcon />}
          color="#388e3c"
          onClick={() => navigate('/school-management')}
        />
        <DashboardCard
          title="Total Users"
          value={stats?.totalUsers || 0}
          icon={<PeopleIcon />}
          color="#f57c00"
          onClick={() => navigate('/users')}
        />
        <DashboardCard
          title="Active Subscriptions"
          value={stats?.activeSchools || 0}
          icon={<PaymentIcon />}
          color="#7b1fa2"
          onClick={() => navigate('/subscription-management')}
        />
        <DashboardCard
          title="System Health"
          value={stats?.systemHealth || 'unknown'}
          icon={<SecurityIcon />}
          color={getHealthColor(stats?.systemHealth || 'unknown')}
          isText={true}
          // Optionally add a navigation or leave as no-op
        />
      </Box>

      {/* Graph Visualizations: Revenue and NRR Trend */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Paper sx={{ flex: 3, p: 3, minWidth: 0 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1E3A8A', fontWeight: 600 }}>
            Revenue Trend (Last 7 Days)
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#1976d2" strokeWidth={3} dot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
        <Paper sx={{ flex: 2, p: 3, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#f57c00', fontWeight: 600 }}>
            NRR Trend (Last 7 Months)
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={recognitionStats?.nrr_trend || []} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis domain={[0, 200]} tickFormatter={v => `${v}%`} />
              <Tooltip formatter={v => `${v}%`} />
              <Legend />
              <Line type="monotone" dataKey="nrr" stroke="#f57c00" strokeWidth={3} dot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* Recently Applied Schools Widget */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Recently Applied Schools
        </Typography>
        {applicationsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 80 }}>
            <CircularProgress size={32} />
          </Box>
        ) : recentApplications.length === 0 ? (
          <Typography color="text.secondary">No recent applications.</Typography>
        ) : (
          <List>
            {recentApplications.map(app => (
              <ListItem key={app.id} sx={{ borderBottom: '1px solid #eee' }}
                secondaryAction={
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => openReviewModal(app)}
                  >
                    Review
                  </Button>
                }
              >
                <ListItemIcon>
                  <SchoolIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={<>
                    <b>{app.school_name}</b> <Chip label={app.status} size="small" color={app.status === 'pending' ? 'warning' : app.status === 'approved' ? 'success' : 'error'} sx={{ ml: 1 }} />
                  </>}
                  secondary={<>
                    <span>Contact: {app.contact_name} ({app.contact_email}{app.contact_phone ? `, ${app.contact_phone}` : ''})</span><br />
                    <span>Applied: {formatDate(app.created_at)}</span>
                  </>}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Review Modal */}
      <Dialog open={reviewModalOpen} onClose={closeReviewModal} maxWidth="sm" fullWidth>
        <DialogTitle>Review School Application</DialogTitle>
        <DialogContent dividers>
          {selectedApplication && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <b>School Name:</b> {selectedApplication.school_name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <b>Contact Name:</b> {selectedApplication.contact_name}<br />
                <b>Email:</b> {selectedApplication.contact_email}<br />
                {selectedApplication.contact_phone && <><b>Phone:</b> {selectedApplication.contact_phone}<br /></>}
                {selectedApplication.message && <><b>Message:</b> {selectedApplication.message}<br /></>}
                <b>Applied At:</b> {formatDate(selectedApplication.created_at)}
              </Typography>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="review-status-label">Decision</InputLabel>
                <Select
                  labelId="review-status-label"
                  value={reviewStatus}
                  label="Decision"
                  onChange={e => setReviewStatus(e.target.value as 'approved' | 'rejected')}
                  disabled={reviewSubmitting}
                >
                  <MenuItem value="approved">Approve</MenuItem>
                  <MenuItem value="rejected">Reject</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Reviewer Comment (optional)"
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 2 }}
                value={reviewMessage}
                onChange={e => setReviewMessage(e.target.value)}
                disabled={reviewSubmitting}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReviewModal} disabled={reviewSubmitting}>Cancel</Button>
          <Button
            onClick={handleReviewSubmit}
            variant="contained"
            color={reviewStatus === 'approved' ? 'primary' : 'error'}
            disabled={!reviewStatus || reviewSubmitting}
          >
            {reviewSubmitting ? <CircularProgress size={20} /> : reviewStatus === 'approved' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recent Platform Activities */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <AnalyticsIcon sx={{ mr: 1 }} />
          Recent Platform Activities
        </Typography>
        <Typography variant="body2" color="text.secondary">(Coming soon: logins, payments, new schools, etc.)</Typography>
      </Paper>

      {/* Management Sections */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 3, 
        mt: 2,
        '& > *': {
          flex: '1 1 350px',
          minWidth: '350px'
        }
      }}>
        <Card sx={{ background: 'linear-gradient(135deg, #1976d215 0%, #1976d208 100%)', border: '1px solid #1976d220' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SchoolIcon sx={{ color: '#1976d2', mr: 1, fontSize: 28 }} />
              <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
                Schools Management
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Create, edit, and manage all schools in the system
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<ViewIcon />}
              onClick={() => navigate('/school-management')}
              sx={{ backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1976d2' } }}
            >
              View All
            </Button>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #388e3c15 0%, #388e3c08 100%)', border: '1px solid #388e3c20' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PeopleIcon sx={{ color: '#388e3c', mr: 1, fontSize: 28 }} />
              <Typography variant="h6" sx={{ color: '#388e3c', fontWeight: 600 }}>
                Users Management
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Manage all users, roles, and permissions across the system
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<ViewIcon />}
              onClick={() => navigate('/users')}
              sx={{ backgroundColor: '#388e3c', '&:hover': { backgroundColor: '#388e3c' } }}
            >
              View All
            </Button>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #9c27b015 0%, #9c27b008 100%)', border: '1px solid #9c27b020' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PaymentIcon sx={{ color: '#9c27b0', mr: 1, fontSize: 28 }} />
              <Typography variant="h6" sx={{ color: '#9c27b0', fontWeight: 600 }}>
                Subscription Management
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Manage subscription plans, activation tokens, and academic terms
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<ViewIcon />}
              onClick={() => navigate('/subscription-management')}
              sx={{ backgroundColor: '#9c27b0', '&:hover': { backgroundColor: '#9c27b0' } }}
            >
              Manage Subscriptions
            </Button>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #ff572215 0%, #ff572208 100%)', border: '1px solid #ff572220' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ReceiptIcon sx={{ color: '#ff5722', mr: 1, fontSize: 28 }} />
              <Typography variant="h6" sx={{ color: '#ff5722', fontWeight: 600 }}>
                Invoice Management
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Generate, view, and manage invoices for school subscriptions
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<ViewIcon />}
              onClick={() => navigate('/invoices')}
              sx={{ backgroundColor: '#ff5722', '&:hover': { backgroundColor: '#ff5722' } }}
            >
              View Invoices
            </Button>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #e91e6315 0%, #e91e6308 100%)', border: '1px solid #e91e6320' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SecurityIcon sx={{ color: '#e91e63', mr: 1, fontSize: 28 }} />
              <Typography variant="h6" sx={{ color: '#e91e63', fontWeight: 600 }}>
                Token Management
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Create, edit, and manage activation tokens for school subscriptions
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<ViewIcon />}
              onClick={() => navigate('/token-management')}
              sx={{ backgroundColor: '#e91e63', '&:hover': { backgroundColor: '#e91e63' } }}
            >
              Manage Tokens
            </Button>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #4caf5015 0%, #4caf5008 100%)', border: '1px solid #4caf5020' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AccountBalanceIcon sx={{ color: '#4caf50', mr: 1, fontSize: 28 }} />
              <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 600 }}>
                M-Pesa Transactions
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Monitor and manage all M-Pesa payment transactions across the system
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<ViewIcon />}
              onClick={() => navigate('/mpesa-transactions')}
              sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#4caf50' } }}
            >
              View Transactions
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default SuperAdminDashboard;